import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useContent } from '../contexts/ContentContext'
import { useCollectionImages } from '../hooks/useCollectionImages'
import { useScrollAnimation } from '../hooks/useScrollAnimation'

const Collections: React.FC = () => {
  const navigate = useNavigate()
  const { content, isLoading } = useContent()
  const { images: boysImages, isLoading: boysLoading } = useCollectionImages('boys')
  const { images: girlsImages, isLoading: girlsLoading } = useCollectionImages('girls')
  const { ref, isVisible } = useScrollAnimation()

  // Estados para el carrusel
  const [boysCurrentIndex, setBoysCurrentIndex] = useState(0)
  const [girlsCurrentIndex, setGirlsCurrentIndex] = useState(0)

  // Función para obtener imágenes activas ordenadas
  const getActiveImages = (images: any[]) => {
    return images
      .filter(img => img.is_active)
      .sort((a, b) => a.order_index - b.order_index)
  }

  const activeBoysImages = getActiveImages(boysImages)
  const activeGirlsImages = getActiveImages(girlsImages)

  // Auto-rotación del carrusel cada 3 segundos
  useEffect(() => {
    if (activeBoysImages.length > 1) {
      const interval = setInterval(() => {
        setBoysCurrentIndex((prev) => (prev + 1) % activeBoysImages.length)
      }, 3000)
      return () => clearInterval(interval)
    }
  }, [activeBoysImages.length])

  useEffect(() => {
    if (activeGirlsImages.length > 1) {
      const interval = setInterval(() => {
        setGirlsCurrentIndex((prev) => (prev + 1) % activeGirlsImages.length)
      }, 3000)
      return () => clearInterval(interval)
    }
  }, [activeGirlsImages.length])

  // Función para obtener la imagen actual del carrusel
  const getCurrentImage = (images: any[], currentIndex: number) => {
    if (images.length === 0) return null
    return images[currentIndex] || images[0]
  }

  const boysCurrentImage = getCurrentImage(activeBoysImages, boysCurrentIndex)
  const girlsCurrentImage = getCurrentImage(activeGirlsImages, girlsCurrentIndex)

  if (isLoading || boysLoading || girlsLoading) {
    return (
      <section id="colecciones" className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
            <p className="mt-4 text-gray-600">Cargando colecciones...</p>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section id="colecciones" className="section-padding bg-white" ref={ref}>
      <div className="container-max">
        <div className={`text-center mb-16 ${isVisible ? 'animate-fade-in-up' : ''}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            {content.collections?.title || 'Nuestras Colecciones'}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {content.collections?.subtitle || 'Descubre nuestra amplia gama de ropa infantil diseñada para la comodidad y el estilo de tus pequeños'}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Colección Niños */}
          <div className={`text-center ${isVisible ? 'animate-fade-in-up' : ''}`} style={isVisible ? { animationDelay: '0ms' } : {}}>
            <div className="rounded-2xl mb-6 overflow-hidden">
              <div
                onClick={() => navigate('/coleccion-ninos')}
                className="aspect-[3/4] w-full bg-blue-300 flex items-center justify-center overflow-hidden relative cursor-pointer hover:opacity-90 transition-opacity"
              >
                {boysCurrentImage ? (
                  <img
                    src={boysCurrentImage.full_image_url || boysCurrentImage.image_url}
                    alt={boysCurrentImage.alt_text}
                    className="w-full h-full object-cover transition-opacity duration-500"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement
                      target.style.display = 'none'
                      target.nextElementSibling?.classList.remove('hidden')
                    }}
                  />
                ) : null}
                <span className={`text-6xl ${boysCurrentImage ? 'hidden' : ''}`}>👦</span>

                {/* Indicadores del carrusel */}
                {activeBoysImages.length > 1 && (
                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
                    {activeBoysImages.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setBoysCurrentIndex(index)}
                        className={`w-2 h-2 rounded-full transition-colors duration-200 ${index === boysCurrentIndex ? 'bg-white' : 'bg-white/50'
                          }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
            <h3 className="text-3xl font-bold text-gray-800 mb-4">
              {content.collections?.boys?.title || 'Colección Niños'}
            </h3>
            <p className="text-lg text-gray-600 mb-6">
              {content.collections?.boys?.description || 'Ropa cómoda y resistente para los más activos. Diseños modernos que combinan estilo y funcionalidad.'}
            </p>
            <button
              className="bg-black text-white font-semibold py-3 px-6 rounded-lg hover:bg-gray-800 transition-colors duration-200"
              onClick={() => navigate('/coleccion-ninos')}
            >
              Ver Colección
            </button>
          </div>

          {/* Colección Niñas */}
          <div className={`text-center ${isVisible ? 'animate-fade-in-up' : ''}`} style={isVisible ? { animationDelay: '100ms' } : {}}>
            <div className="rounded-2xl mb-6 overflow-hidden">
              <div
                onClick={() => navigate('/coleccion-ninas')}
                className="aspect-[3/4] w-full bg-pink-300 flex items-center justify-center overflow-hidden relative cursor-pointer hover:opacity-90 transition-opacity"
              >
                {girlsCurrentImage ? (
                  <img
                    src={girlsCurrentImage.full_image_url || girlsCurrentImage.image_url}
                    alt={girlsCurrentImage.alt_text}
                    className="w-full h-full object-cover transition-opacity duration-500"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement
                      target.style.display = 'none'
                      target.nextElementSibling?.classList.remove('hidden')
                    }}
                  />
                ) : null}
                <span className={`text-6xl ${girlsCurrentImage ? 'hidden' : ''}`}>👧</span>

                {/* Indicadores del carrusel */}
                {activeGirlsImages.length > 1 && (
                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
                    {activeGirlsImages.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setGirlsCurrentIndex(index)}
                        className={`w-2 h-2 rounded-full transition-colors duration-200 ${index === girlsCurrentIndex ? 'bg-white' : 'bg-white/50'
                          }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
            <h3 className="text-3xl font-bold text-gray-800 mb-4">
              {content.collections?.girls?.title || 'Colección Niñas'}
            </h3>
            <p className="text-lg text-gray-600 mb-6">
              {content.collections?.girls?.description || 'Vestidos, blusas y conjuntos elegantes que realzan la personalidad única de cada niña.'}
            </p>
            <button
              className="bg-black text-white font-semibold py-3 px-6 rounded-lg hover:bg-gray-800 transition-colors duration-200"
              onClick={() => navigate('/coleccion-ninas')}
            >
              Ver Colección
            </button>
          </div>
        </div>

        {/* Características destacadas */}
        <div className={`mt-20 grid md:grid-cols-3 gap-8 ${isVisible ? 'animate-fade-in-up' : ''}`} style={isVisible ? { animationDelay: '200ms' } : {}}>
          <div className="text-center">
            <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">✨</span>
            </div>
            <h4 className="text-xl font-semibold text-gray-800 mb-2">
              Calidad Premium
            </h4>
            <p className="text-gray-600">
              Materiales de primera calidad que garantizan durabilidad y comodidad.
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🎨</span>
            </div>
            <h4 className="text-xl font-semibold text-gray-800 mb-2">
              Diseños Únicos
            </h4>
            <p className="text-gray-600">
              Cada prenda está diseñada pensando en la personalidad y comodidad de los niños.
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🌱</span>
            </div>
            <h4 className="text-xl font-semibold text-gray-800 mb-2">
              Sostenible
            </h4>
            <p className="text-gray-600">
              Comprometidos con el medio ambiente usando materiales eco-amigables.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Collections

